/**
 * Service for making authenticated API calls to the backend
 */

import { apiRequest } from '../config/authConfig';
import { getAccessToken } from '../hooks/useAuth';
import { parseServerError } from '../utils/errorUtils';

// Set API base URL from environment variable (required)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Helper function to create a user-friendly error from a fetch response
 * @param {Response} response - The fetch response object
 * @param {string} url - The URL that was called
 * @param {string} method - The HTTP method used
 * @returns {Promise<Error>} Enhanced error with user-friendly message
 */
export const createErrorFromResponse = async (response, url, method = 'GET') => {
    const errorText = await response.text();
    
    // Try to parse the error response as JSON
    let errorData = null;
    try {
        errorData = JSON.parse(errorText);
    } catch (e) {
        // Not JSON, use text as-is
        errorData = { message: errorText };
    }
    
    // Create an error object that mimics axios error structure for parseServerError
    const error = new Error();
    error.response = {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
        config: { url, method }
    };
    
    // Use the centralized error parser
    const userFriendlyMessage = parseServerError(error);
    
    // Log full details for debugging
    console.error('API Error Details:', {
        status: response.status,
        statusText: response.statusText,
        endpoint: url,
        method: method,
        response: errorData,
        errorText: errorText // Include raw error text
    });
    
    // Create enhanced error with user-friendly message
    const enhancedError = new Error(userFriendlyMessage);
    enhancedError.response = error.response;
    enhancedError.rawError = errorData; // Add raw error data
    return enhancedError;
};

/**
 * Generic API call function with JWT token authentication
 * @param {string} accessToken - JWT access token
 * @param {string} endpoint - API endpoint (relative to base URL)
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise} - Response data
 */
export const makeAuthenticatedRequest = async (accessToken, endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Create request options with proper spreading order
    const requestOptions = {
        ...options,              // Spread basic options first
        mode: 'cors',           // Ensure CORS is set
        headers: {              // Merge headers with priority to defaults
            ...options.headers,  // Service-specific headers first
            'Authorization': `Bearer ${accessToken}`,  // Always override with auth token
            'Accept': 'application/json'              // Always ensure Accept is set
        }
    };

    // Ensure method defaults to GET if not specified
    if (!requestOptions.method) {
        requestOptions.method = 'GET';
    };
    
    try {
        const response = await fetch(url, requestOptions);
        
        if (!response.ok) {
            throw await createErrorFromResponse(response, url, requestOptions.method);
        }
        
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
            return await response.json();
        } else {
            // Return as text for non-JSON responses (including text/plain)
            return await response.text();
        }
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
};

/**
 * Get user profile from backend
 * @param {string} accessToken - JWT access token
 * @returns {Promise} - User profile data
 */
export const getUserProfile = async (accessToken) => {
    return makeAuthenticatedRequest(accessToken, '/api/v1/user/profile');
};

/**
 * Test endpoint connectivity
 * @param {string} accessToken - JWT access token
 * @returns {Promise} - Test response
 */
export const testConnection = async (accessToken) => {
    try {
        return await makeAuthenticatedRequest(accessToken, '/api/v1/user/profile');
    } catch (error) {
        console.error('Connection test failed:', error);
        throw error;
    }
};

/**
 * Test public health endpoint (no authentication required)
 * @returns {Promise} - Health response
 */
export const testHealthEndpoint = async () => {
    try {
        console.log(`Testing public health endpoint: ${API_BASE_URL}/public/v1/health`);
        
        const response = await fetch(`${API_BASE_URL}/public/v1/health`, {
            method: 'GET',
            mode: 'cors',
            headers: {
                'Accept': 'application/json'
            }
        });

        console.log(`Health endpoint response status: ${response.status}`);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Health Endpoint Error Details:');
            console.error('- Status Code:', response.status);
            console.error('- Status Text:', response.statusText);
            console.error(`- Endpoint: ${API_BASE_URL}/public/v1/health`);
            console.error('- Response Text:', errorText);
            
            throw new Error(`Health Check Failed\nStatus: ${response.status} ${response.statusText}\nEndpoint: ${API_BASE_URL}/public/v1/health\nResponse: ${errorText}`);
        }

        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            console.log('Health endpoint response:', data);
            return data;
        } else {
            const text = await response.text();
            console.log('Health endpoint response (text):', text);
            return text;
        }
    } catch (error) {
        console.error('Health endpoint test failed:', error);
        throw error;
    }
};

/**
 * Test JWT debug endpoint (with authorization header)
 * @param {string} accessToken - JWT access token
 * @returns {Promise} - JWT debug response
 */
export const testJwtDebugEndpoint = async (accessToken) => {
    try {
        console.log(`Testing JWT debug endpoint: ${API_BASE_URL}/public/v1/debug-jwt`);
        
        const response = await fetch(`${API_BASE_URL}/public/v1/debug-jwt`, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });

        console.log(`JWT debug endpoint response status: ${response.status}`);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('JWT Debug Endpoint Error Details:');
            console.error('- Status Code:', response.status);
            console.error('- Status Text:', response.statusText);
            console.error(`- Endpoint: ${API_BASE_URL}/public/v1/debug-jwt`);
            console.error('- Response Text:', errorText);
            
            throw new Error(`JWT Debug Failed\nStatus: ${response.status} ${response.statusText}\nEndpoint: ${API_BASE_URL}/public/v1/debug-jwt\nResponse: ${errorText}`);
        }

        const data = await response.json();
        console.log('JWT debug endpoint response:', data);
        return data;
    } catch (error) {
        console.error('JWT debug endpoint test failed:', error);
        throw error;
    }
};

/**
 * Get current user info (protected endpoint)
 * @param {string} accessToken - JWT access token
 * @returns {Promise} - Current user data
 */
export const getCurrentUser = async (accessToken) => {
    return makeAuthenticatedRequest(accessToken, '/api/v1/current-user');
};

/**
 * Get debug auth info (protected endpoint)
 * @param {string} accessToken - JWT access token
 * @returns {Promise} - Debug auth data
 */
export const getDebugAuth = async (accessToken) => {
    return makeAuthenticatedRequest(accessToken, '/api/v1/debug/auth');
};

/**
 * Generic CRUD operations
 */
export const backendApi = {
    get: (accessToken, endpoint) => makeAuthenticatedRequest(accessToken, endpoint),
    
    post: (accessToken, endpoint, data) => 
        makeAuthenticatedRequest(accessToken, endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        }),
    
    put: (accessToken, endpoint, data) => 
        makeAuthenticatedRequest(accessToken, endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        }),
    
    delete: (accessToken, endpoint) => 
        makeAuthenticatedRequest(accessToken, endpoint, {
            method: 'DELETE'
        })
};
